import { cosmiconfig } from 'cosmiconfig';
import { z } from 'zod';
import { VibeGuardConfigSchema, type VibeGuardConfig } from '../types/index.js';

const explorer = cosmiconfig('vibeguard', {
  searchPlaces: [
    'vibeguard.config.js',
    'vibeguard.config.mjs',
    'vibeguard.config.cjs',
    'vibeguard.config.json',
    'vibeguard.config.yaml',
    'vibeguard.config.yml',
    '.vibeguardrc',
    '.vibeguardrc.json',
    '.vibeguardrc.yaml',
    '.vibeguardrc.yml',
    'package.json',
  ],
});

export interface LoadedConfig {
  config: VibeGuardConfig;
  filepath: string;
  isEmpty: boolean;
}

export async function loadConfig(cwd: string = process.cwd()): Promise<LoadedConfig | null> {
  try {
    const result = await explorer.search(cwd);

    if (!result || result.isEmpty) {
      return null;
    }

    const validated = VibeGuardConfigSchema.parse(result.config);

    return {
      config: validated,
      filepath: result.filepath,
      isEmpty: false,
    };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const issues = error.issues.map((i) => `  - ${i.path.join('.')}: ${i.message}`).join('\n');
      throw new Error(`Invalid vibeguard config:\n${issues}`);
    }
    throw error;
  }
}

export async function loadConfigFromFile(filepath: string): Promise<LoadedConfig> {
  const result = await explorer.load(filepath);

  if (!result || result.isEmpty) {
    throw new Error(`Config file is empty: ${filepath}`);
  }

  const validated = VibeGuardConfigSchema.parse(result.config);

  return {
    config: validated,
    filepath: result.filepath,
    isEmpty: false,
  };
}

export function getDefaultConfig(): VibeGuardConfig {
  return {
    level: 'standard',
    ci: {
      failOn: 'error',
      reportFormat: 'console',
    },
    tools: {
      eslint: true,
      semgrep: true,
      gitleaks: true,
      osvScanner: true,
    },
  };
}
